import { useMemo, useState } from 'react'

import PhoneInputWithCountry from '../ui/PhoneInputWithCountry'
import { RoundedButton } from '../ui/roundButton'
import { createAccount } from '../../services/scout'
import { openSupportForm } from '../../services/support'
import { getPhoneDigits, isValidBrazilianPhone } from '../../utils'
import manageErrors from '../../lib/error-management'

const registrationTips = [
	{
		title: 'Comece pequeno',
		description: 'Não é necessário cadastrar toda a equipe agora. Um número já permite validar o Scout.',
		tone: 'green',
	},
	{
		title: 'Escolha o número com mais movimento',
		description: 'Quanto mais conversas, mais rápido você verá buscas, padrões e pontos de atenção.',
		tone: 'blue',
	},
	{
		title: 'Evite perder visibilidade',
		description: 'Sem número ativo, sua empresa volta a operar WhatsApp sem histórico centralizado.',
		tone: 'rose',
	},
]

const nextSteps = [
	{
		title: 'Cadastrar número',
		description: 'Você informa o telefone que será monitorado.',
	},
	{
		title: 'Escanear QR Code',
		description: 'O sistema gera um QR Code para conexão pelo WhatsApp.',
	},
	{
		title: 'Ver conversas',
		description: 'Após conectar, o Scout começa a organizar mensagens, buscas e alertas.',
	},
]

const getUserEmail = (user) => {
	return user?.email || user?.username || user?.profile?.email || ''
}

const getUserId = (user) => {
	return user?.id || user?.pk || user?.profile?.user || user?.profile?.id || ''
}

const EmptyScoutNumbersGate = ({ onCreated, showSnackbar, user }) => {
	const [submitting, setSubmitting] = useState(false)
	const [phoneCountryCode, setPhoneCountryCode] = useState('BR')
	const [form, setForm] = useState({
		phone: '',
	})

	const phoneDigits = getPhoneDigits(form.phone)
	const phoneIsValid = phoneDigits.length > 0 && isValidBrazilianPhone(form.phone, phoneCountryCode)
	const defaultOwnerId = useMemo(() => getUserId(user), [user])
	const defaultEmail = useMemo(() => getUserEmail(user), [user])
	const formIsValid = phoneIsValid && defaultOwnerId && defaultEmail

	const handleSubmit = async (event) => {
		event.preventDefault()

		if (!formIsValid || submitting) {
			return
		}

		try {
			setSubmitting(true)
			const response = await createAccount({
				name: form.phone.replace('+', ''),
				phone: form.phone,
				owner: defaultOwnerId,
				email: defaultEmail,
			})

			if (response.status === 201) {
				showSnackbar?.({ message: 'Número cadastrado com sucesso. Agora gere o QR Code para conectar.', severity: 'success' })
				await onCreated?.()
				return
			}

			showSnackbar?.({ message: manageErrors({ error: response.data }), severity: 'warning' })
		} catch (error) {
			showSnackbar?.({ message: error.message || 'Não foi possível cadastrar o número.', severity: 'error' })
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<section className="scout-empty-numbers-gate">
			<div className="scout-empty-numbers-gate__shell">
				<header className="scout-empty-numbers-gate__top">
					<div>
						<strong>Etapa 1 de 3</strong>
						<span>Cadastrar número</span>
					</div>
					<span>Depois: conectar via QR Code e visualizar conversas</span>
				</header>

				<div className="scout-empty-numbers-gate__progress" aria-hidden="true">
					<span />
				</div>

				<div className="scout-empty-numbers-gate__intro">
					<h2>Cadastre 1 número para retomar seu teste do Scout</h2>
					<p>Você ainda não possui nenhum telefone monitorado ativo. Cadastre um número agora e, em seguida, escaneie o QR Code para começar a visualizar as conversas.</p>
				</div>

				<p className="scout-empty-numbers-gate__warning">
					Enquanto nenhum número estiver cadastrado e conectado, o Scout não consegue gerar histórico, buscas, alertas ou evidências de controle.
				</p>

				<div className="scout-empty-numbers-gate__grid">
					<form className="scout-empty-numbers-card scout-empty-numbers-form" onSubmit={handleSubmit}>
						<h3>Número que será monitorado</h3>
						<p>Comece com apenas 1 número. Recomendamos cadastrar primeiro o WhatsApp que mais conversa com clientes.</p>

						<label htmlFor="first-scout-number-phone">Número de WhatsApp</label>
						<PhoneInputWithCountry
							id="first-scout-number-phone"
							value={form.phone}
							background="#fff"
							placeholder="21 99999-9999"
							onChange={(value) => setForm((currentForm) => ({ ...currentForm, phone: value }))}
							onCountryChange={(country) => setPhoneCountryCode(country.code)}
						/>
						{phoneDigits.length > 0 && !phoneIsValid ? (
							<small className="scout-empty-numbers-form__error">Telefone do Brasil deve ter DDD válido e 8 ou 9 dígitos.</small>
						) : (
							<small>Informe o número que será conectado ao Scout. O WhatsApp continuará sendo usado normalmente no celular.</small>
						)}

						<RoundedButton
							type="submit"
							className="scout-empty-numbers-form__submit"
							disabled={!formIsValid || submitting}
						>
							{submitting ? 'Cadastrando...' : 'Cadastrar número e gerar QR Code'}
						</RoundedButton>

						<button type="button" className="scout-empty-numbers-form__help" onClick={openSupportForm}>
							Preciso de ajuda para escolher o número
						</button>
					</form>

					<aside className="scout-empty-numbers-card scout-empty-numbers-tips">
						<h3>Qual número cadastrar primeiro?</h3>
						<p>Para perceber valor mais rápido, escolha um número que tenha conversas reais com clientes, leads, pacientes, fornecedores ou parceiros.</p>

						<div className="scout-empty-numbers-tips__list">
							{registrationTips.map((tip) => (
								<article key={tip.title} className={`scout-empty-numbers-tip scout-empty-numbers-tip--${tip.tone}`}>
									<strong>{tip.title}</strong>
									<span>{tip.description}</span>
								</article>
							))}
						</div>
					</aside>
				</div>

				<section className="scout-empty-numbers-next">
					<h3>O que acontece depois do cadastro?</h3>
					<div>
						{nextSteps.map((step, index) => (
							<article key={step.title}>
								<span>{index + 1}</span>
								<strong>{step.title}</strong>
								<p>{step.description}</p>
							</article>
						))}
					</div>
				</section>

				<footer className="scout-empty-numbers-gate__footer">
					A conexão usa o recurso de dispositivos conectados do WhatsApp. O colaborador continua usando o aplicativo normalmente no celular. Após cadastrar o número, o próximo passo será escanear o QR Code exibido na tela.
				</footer>
			</div>
		</section>
	)
}

export default EmptyScoutNumbersGate
